import React, { useState, useMemo } from 'react';
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker
} from 'react-simple-maps';

// TopoJSON do Brasil (usando o fornecido pela biblioteca)
const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// Estados brasileiros
const estadosBrasil = {
  'AC': { nome: 'Acre' },
  'AL': { nome: 'Alagoas' },
  'AP': { nome: 'Amapá' },
  'AM': { nome: 'Amazonas' },
  'BA': { nome: 'Bahia' },
  'CE': { nome: 'Ceará' },
  'DF': { nome: 'Distrito Federal' },
  'ES': { nome: 'Espírito Santo' },
  'GO': { nome: 'Goiás' },
  'MA': { nome: 'Maranhão' },
  'MT': { nome: 'Mato Grosso' },
  'MS': { nome: 'Mato Grosso do Sul' },
  'MG': { nome: 'Minas Gerais' },
  'PA': { nome: 'Pará' },
  'PB': { nome: 'Paraíba' },
  'PR': { nome: 'Paraná' },
  'PE': { nome: 'Pernambuco' },
  'PI': { nome: 'Piauí' },
  'RJ': { nome: 'Rio de Janeiro' },
  'RN': { nome: 'Rio Grande do Norte' },
  'RS': { nome: 'Rio Grande do Sul' },
  'RO': { nome: 'Rondônia' },
  'RR': { nome: 'Roraima' },
  'SC': { nome: 'Santa Catarina' },
  'SP': { nome: 'São Paulo' },
  'SE': { nome: 'Sergipe' },
  'TO': { nome: 'Tocantins' }
};

// Mapeamento de nomes de estados para siglas
const mapearEstadoParaSigla = (nomeEstado) => {
  if (!nomeEstado) return null;
  
  const estadoUpper = nomeEstado.toUpperCase().trim();
  
  // Mapeamento direto por sigla
  if (estadosBrasil[estadoUpper]) return estadoUpper;
  
  // Mapeamento por nome completo
  const mapeamentoNomes = {
    'ACRE': 'AC', 'ALAGOAS': 'AL', 'AMAPÁ': 'AP', 'AMAPA': 'AP',
    'AMAZONAS': 'AM', 'BAHIA': 'BA', 'CEARÁ': 'CE', 'CEARA': 'CE',
    'DISTRITO FEDERAL': 'DF', 'ESPÍRITO SANTO': 'ES', 'ESPIRITO SANTO': 'ES',
    'GOIÁS': 'GO', 'GOIAS': 'GO', 'MARANHÃO': 'MA', 'MARANHAO': 'MA',
    'MATO GROSSO': 'MT', 'MATO GROSSO DO SUL': 'MS', 'MINAS GERAIS': 'MG',
    'PARÁ': 'PA', 'PARA': 'PA', 'PARAÍBA': 'PB', 'PARAIBA': 'PB',
    'PARANÁ': 'PR', 'PARANA': 'PR', 'PERNAMBUCO': 'PE',
    'PIAUÍ': 'PI', 'PIAUI': 'PI', 'RIO DE JANEIRO': 'RJ',
    'RIO GRANDE DO NORTE': 'RN', 'RIO GRANDE DO SUL': 'RS',
    'RONDÔNIA': 'RO', 'RONDONIA': 'RO', 'RORAIMA': 'RR',
    'SANTA CATARINA': 'SC', 'SÃO PAULO': 'SP', 'SAO PAULO': 'SP',
    'SERGIPE': 'SE', 'TOCANTINS': 'TO'
  };
  
  return mapeamentoNomes[estadoUpper] || null;
};

const BrazilMap = ({ accessData }) => {
  const [hoveredState, setHoveredState] = useState(null);
  
  // Criar mapa de acessos por estado usando useMemo
  const { acessosPorEstado, maxAcessos } = useMemo(() => {
    const acessos = {};
    let max = 0;
    
    if (accessData && accessData.length > 0) {
      console.log('📊 Dados de acesso recebidos:', accessData);
      accessData.forEach(item => {
        const sigla = mapearEstadoParaSigla(item.region) || item.region?.toUpperCase();
        if (sigla && estadosBrasil[sigla]) {
          if (!acessos[sigla]) {
            acessos[sigla] = 0;
          }
          acessos[sigla] += item.count;
          if (acessos[sigla] > max) {
            max = acessos[sigla];
          }
        } else {
          console.warn('⚠️ Estado não reconhecido:', item.region);
        }
      });
      console.log('📈 Acessos por estado processados:', acessos, 'Max:', max);
    }
    
    return { acessosPorEstado: acessos, maxAcessos: max };
  }, [accessData]);
  
  // Função para obter cor baseada na quantidade de acessos
  const getColor = (sigla) => {
    const acessos = acessosPorEstado[sigla] || 0;
    if (acessos === 0) {
      return '#f0f0f0'; // Branco/cinza claro para estados sem acessos
    }
    
    // Escala de cores: quanto mais acessos, mais escuro/intenso
    const intensidade = Math.min(acessos / Math.max(maxAcessos, 1), 1);
    const r = Math.floor(102 + (153 * (1 - intensidade))); // 102-255
    const g = Math.floor(126 + (129 * (1 - intensidade))); // 126-255
    const b = Math.floor(234 + (21 * (1 - intensidade)));  // 234-255
    
    return `rgb(${r}, ${g}, ${b})`;
  };
  
  // Usar GeoJSON do Brasil
  // Tentando várias URLs conhecidas de GeoJSON do Brasil
  const brazilGeoUrl = "https://raw.githubusercontent.com/codeforamerica/click_that_hood/master/public/data/brazil-states.geojson";
  
  // Fallback: se a URL acima não funcionar, podemos usar um TopoJSON do mundo e filtrar
  // Por enquanto, vamos tentar a URL acima
  
  return (
    <div style={{ width: '100%', marginTop: '20px' }}>
      <ComposableMap
        projection="geoMercator"
        projectionConfig={{
          center: [-55, -15],
          scale: 800
        }}
        style={{ width: '100%', height: '400px' }}
      >
        <Geographies geography={brazilGeoUrl}>
          {({ geographies }) =>
            geographies.map((geo) => {
              // Tentar identificar o estado pelo nome ou propriedades do GeoJSON
              const nomeEstado = geo.properties.name || geo.properties.NAME || geo.properties.nome || '';
              const sigla = mapearEstadoParaSigla(nomeEstado) || 
                          geo.properties.sigla || 
                          geo.properties.SIGLA ||
                          geo.properties.abbrev ||
                          geo.properties.ABBREV;
              
              if (!sigla || !estadosBrasil[sigla]) {
                console.warn('⚠️ Estado não identificado no GeoJSON:', nomeEstado, geo.properties);
                return null;
              }
              
              const acessos = acessosPorEstado[sigla] || 0;
              const cor = getColor(sigla);
              const isHovered = hoveredState === sigla;
              const estadoNome = estadosBrasil[sigla].nome;
              
              return (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill={isHovered ? '#0ad596' : cor}
                  stroke="#fff"
                  strokeWidth={1}
                  style={{
                    default: {
                      outline: 'none',
                      cursor: 'pointer',
                      transition: 'fill 0.2s'
                    },
                    hover: {
                      outline: 'none',
                      fill: '#0ad596'
                    },
                    pressed: {
                      outline: 'none'
                    }
                  }}
                  onMouseEnter={() => {
                    setHoveredState(sigla);
                  }}
                  onMouseLeave={() => {
                    setHoveredState(null);
                  }}
                >
                  <title>{`${estadoNome}: ${acessos} ${acessos === 1 ? 'acesso' : 'acessos'}`}</title>
                </Geography>
              );
            })
          }
        </Geographies>
      </ComposableMap>
      
      {/* Legenda */}
      <div style={{ marginTop: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '20px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <div style={{ width: '20px', height: '20px', backgroundColor: '#f0f0f0', border: '2px solid #ddd', borderRadius: '3px' }}></div>
          <span>Sem acessos</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <div style={{ width: '20px', height: '20px', backgroundColor: 'rgb(102, 126, 234)', borderRadius: '3px' }}></div>
          <span>Com acessos no Brasil (intensidade = quantidade)</span>
        </div>
        {maxAcessos > 0 && (
          <div style={{ fontSize: '12px', color: '#666' }}>
            Máximo: {maxAcessos} {maxAcessos === 1 ? 'acesso' : 'acessos'}
          </div>
        )}
      </div>
    </div>
  );
};

export default BrazilMap;
